import { getMessageDefinitions, getMessageDefinitionByType } from '@k-ucap/message-dictionary';
import type { MessageDefinition } from '@k-ucap/shared';

export interface MessageFilter {
  search?: string;
  category?: string;
  sourceRole?: string;
  targetRole?: string;
  transferMode?: string;
  supportedOnly?: boolean;
}

const matchesSearch = (msg: MessageDefinition, query: string): boolean => {
  const lower = query.toLowerCase();
  return (
    String(msg.messageType).includes(lower) ||
    msg.name.toLowerCase().includes(lower) ||
    msg.displayName.toLowerCase().includes(lower) ||
    msg.category.toLowerCase().includes(lower) ||
    msg.tags.some((t) => t.toLowerCase().includes(lower))
  );
};

export class MessageDictionaryService {
  private readonly messages: MessageDefinition[];

  constructor(messages?: MessageDefinition[]) {
    this.messages = messages ?? getMessageDefinitions();
  }

  getAllMessages(): MessageDefinition[] {
    return this.messages;
  }

  getMessageById(id: string): MessageDefinition | undefined {
    return this.messages.find((m) => m.id === id);
  }

  getMessageByType(messageType: number): MessageDefinition | undefined {
    return getMessageDefinitionByType(messageType) ?? this.messages.find((m) => m.messageType === messageType);
  }

  searchMessages(query: string): MessageDefinition[] {
    if (!query.trim()) return this.messages;
    return this.messages.filter((m) => matchesSearch(m, query));
  }

  filterMessages(filter: MessageFilter): MessageDefinition[] {
    return this.messages.filter((m) => {
      if (filter.supportedOnly && !m.supported) return false;
      if (filter.category && m.category !== filter.category) return false;
      if (filter.sourceRole && m.sourceRole !== filter.sourceRole) return false;
      if (filter.targetRole && m.targetRole !== filter.targetRole) return false;
      if (filter.transferMode && m.transferMode !== filter.transferMode) return false;
      if (filter.search && !matchesSearch(m, filter.search)) return false;
      return true;
    });
  }

  getCategories(): string[] {
    return Array.from(new Set(this.messages.map((m) => m.category))).sort();
  }

  getSourceRoles(): string[] {
    return Array.from(new Set(this.messages.map((m) => m.sourceRole))).sort();
  }

  getTargetRoles(): string[] {
    return Array.from(new Set(this.messages.map((m) => m.targetRole))).sort();
  }

  getTransferModes(): string[] {
    return Array.from(new Set(this.messages.map((m) => m.transferMode))).sort();
  }

  getSupportedMessages(): MessageDefinition[] {
    return this.messages.filter((m) => m.supported);
  }
}

export const createMessageDictionaryService = (): MessageDictionaryService => new MessageDictionaryService();
